/**
 * In-store mode (E10): screen wake lock for Shopping mode. The screen
 * dimming mid-aisle (wet hands, cart handle) is the #1 phone-in-store
 * annoyance; `navigator.wakeLock` keeps it on WHILE THE USER EXPLICITLY
 * OPTED IN -- silently burning battery is hostile, so the caller only
 * passes `active: true` off an explicit toggle.
 *
 * Browser realities handled here:
 *  - Feature detection: `wakeLock` is missing on older Safari/iOS (< 16.4)
 *    and some WebViews. `isWakeLockSupported()` is how callers decide to
 *    HIDE the toggle entirely -- absent API means no errors, no dead
 *    switch.
 *  - Locks auto-release when the tab is backgrounded or the device
 *    sleeps; we re-acquire on `visibilitychange` back to visible.
 *  - `request()` can reject even when the API exists (low battery mode,
 *    permissions policy) -- that's non-fatal and swallowed: the list
 *    still works, the screen just dims like it always did.
 */

import { useEffect } from "react";

interface WakeLockSentinelLike {
  release(): Promise<void>;
}

interface WakeLockNavigator {
  wakeLock?: {
    request(type: "screen"): Promise<WakeLockSentinelLike>;
  };
}

export function isWakeLockSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    (navigator as WakeLockNavigator).wakeLock !== undefined
  );
}

export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !isWakeLockSupported()) return;

    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        const lock = await (
          navigator as WakeLockNavigator
        ).wakeLock!.request("screen");
        if (cancelled) {
          // The effect cleaned up while the request was in flight.
          void lock.release().catch(() => undefined);
          return;
        }
        sentinel = lock;
      } catch {
        // Denied (battery saver, policy) -- non-fatal by design.
      }
    };

    const onVisibilityChange = () => {
      // Locks release automatically when backgrounded; take it back the
      // moment the user returns (pocket -> aisle is the core loop here).
      if (document.visibilityState === "visible") void acquire();
    };

    void acquire();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      void sentinel?.release().catch(() => undefined);
      sentinel = null;
    };
  }, [active]);
}
